import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, X, Printer } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

/**
 * Floating banner shown on print pages launched from the Printcentrum.
 *
 * Reads `?print_session=<id>` from the URL and the matching entry from
 * sessionStorage. Renders a non-printing action bar at the top of the page
 * with "Markeer als geprint" / "Annuleren" buttons. After confirm it POSTs
 * the records and navigates back to /printcentrum.
 *
 * sessionStorage payload (set by PrintCenter before navigating):
 *   {
 *     records: [{order_id, location_id, doc_type}],  // batch to mark
 *     label: "Raaplijst van 3 bestellingen",         // human descr
 *     count: 3,                                       // doc count
 *   }
 */
export default function PrintSessionBanner() {
    const nav = useNavigate();
    const [searchParams] = useSearchParams();
    const sessionId = searchParams.get("print_session") || "";
    const storageKey = sessionId ? `print_session_${sessionId}` : "";
    const [session, setSession] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!sessionId) {
            setSession(null);
            return;
        }
        try {
            const raw = sessionStorage.getItem(storageKey);
            if (raw) setSession(JSON.parse(raw));
        } catch (err) {
            console.warn("[print-session] read failed", err);
            setSession(null);
        }
    }, [sessionId, storageKey]);

    if (!session || !session.records || session.records.length === 0) return null;

    const cleanup = () => {
        try {
            sessionStorage.removeItem(storageKey);
        } catch (err) {
            console.warn("[print-session] cleanup failed", err);
        }
    };

    const onCancel = () => {
        cleanup();
        nav("/printcentrum");
    };

    const onConfirm = async () => {
        setBusy(true);
        try {
            const r = await api.post("/print-records", { records: session.records });
            toast.success(`${r.data.count} document${r.data.count === 1 ? "" : "en"} gemarkeerd als geprint`);
            cleanup();
            nav("/printcentrum");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail ?? "Markeren mislukt"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            className="no-print sticky top-0 z-40 border-b border-florefy-200 bg-white/95 text-ink-900 shadow-lg backdrop-blur"
            data-testid="print-session-banner"
        >
            <div className="max-w-7xl mx-auto px-5 sm:px-7 py-4 flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3 min-w-0">
                    <span className="h-10 w-10 rounded-xl bg-florefy-100 text-florefy-900 flex items-center justify-center shrink-0"><Printer className="h-5 w-5" /></span>
                    <div className="min-w-0">
                        <div className="font-semibold text-sm truncate">
                            {session.label || "Print-sessie actief"}
                        </div>
                        <div className="text-xs text-ink-500 mt-0.5">
                            Stap 1: print het document. Stap 2: bevestig alleen wanneer de printer klaar is.
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <button
                        onClick={onCancel}
                        disabled={busy}
                        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl border border-ink-200 bg-white text-ink-700 text-xs font-medium hover:bg-ink-50 disabled:opacity-40"
                        data-testid="btn-session-cancel"
                    >
                        <X className="h-3.5 w-3.5" />
                        Annuleren
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={busy}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl border border-florefy-900 bg-florefy-900 text-white hover:bg-florefy-800 text-xs font-semibold shadow-sm disabled:opacity-40"
                        data-testid="btn-session-confirm"
                    >
                        <CheckCircle2 className="h-3.5 w-3.5" />
                        {busy ? "Opslaan…" : "Ja, goed geprint"}
                    </button>
                </div>
            </div>
        </div>
    );
}
